// Terms-of-Service constants — the commercial contract between the
// platform-owning entity and each paying tenant. Deliberately NOT in
// popia.ts: that file is the privacy/data-protection track, and mixing a
// commercial agreement into it makes both harder to reason about when one
// changes. The Responsible Party identity is shared, so it is imported.
//
// The version track rides the SAME rail as the privacy policy —
// record_user_consent (mig 505) stores an arbitrary version string as a
// top-level key in users.consents. No migration was needed to add this track.
//
// When the terms materially change, bump TERMS_VERSION (e.g.
// 'terms_of_service_v2'). ConsentGate will then re-prompt every active user
// on their next authenticated page load. The v1 acceptance stays on record
// under its OWN key, 'terms_of_service_v1': the RPC adds the new key and never
// touches the old one. ('<version>__history' only collects a re-acceptance of
// the SAME version — the live body is the baseline's, per fn_source.)
//
// ⚠️ These terms are a best-effort South African default set. They have NOT
// been reviewed by an attorney. Two clauses are genuine commercial decisions
// rather than drafting defaults, and are marked ATTORNEY-REVIEW in
// TermsOfService.tsx: the liability cap (§15) and the Earned-Wage-Access
// characterisation under the National Credit Act (§9). Do not treat the
// merged text as cleared.

import { RESPONSIBLE_PARTY } from '@/lib/popia'
import { isAdmin } from '@/lib/roles'
import type { UserRole } from '@/types/database'

// The version constants live in termsVersion.ts (import-free, so the e2e
// mock can load them under plain Node). Re-exported: import them from here.
export { TERMS_VERSION, TERMS_VERSION_LABEL, TERMS_EFFECTIVE_DATE } from './termsVersion'

// WHO ACCEPTS THE TERMS (Jason's ruling, 6 Oct 2026): the customer's
// ADMINISTRATORS, on behalf of their business — the Tenant is the contracting
// party (Terms §1). Every other user (workers, floor staff, badge + face
// sign-ins) is never asked for the Terms; they see only the privacy prompt.
//
// "Administrator" is the admin tier of the ACTIVE tenant's own membership
// role — `tenant_role` on the current user, which is what current_tenant_role()
// returns, with none of `role`'s fallbacks (a platform SUPER_ADMIN viewing a
// tenant does not accept its contract). The tier itself is roles.ts isAdmin(),
// the client twin of the edge functions' requireAdminTierCaller ADMIN_TIER
// (ADMIN / SUPER_ADMIN / FARM_ADMIN). One predicate for the gate and the
// invite form, so the two can never disagree about who is asked.
export function acceptsTermsForBusiness(tenantRole: UserRole | null | undefined): boolean {
  return isAdmin(tenantRole)
}

// Contracting entity + governing law, surfaced in the page and in the
// Operator Agreement template under docs/legal/. Single source of truth so a
// company-details change does not have to be chased through prose.
export const CONTRACTING_ENTITY = {
  legalName:    RESPONSIBLE_PARTY.legalName,
  // ECTA s43(1) requires a supplier transacting electronically to disclose its
  // legal status, registration number and physical address on the site itself
  // — so these are rendered on /terms, not just held for contracts.
  // Source: CIPC CoR15.1A, registered 30 March 2026.
  registrationNumber: '2026/260656/07',
  registeredAddress:  'Gedeelte 1095, Van die Plaas Hartebeespoort, Brits, North West, 0250',
  postalAddress:      'Posbus 3572, Brits, North West, 0250',
  // Contractual-notice address under the Terms (§21). A contractual notice is
  // NOT a data-subject request, and ideally the two do not share a queue — the
  // privacy inbox is worked to a POPIA s23 clock, a notice of breach or
  // termination is not.
  //
  // INTERIM (2026-09-10, Jason's call): pointed at the POPIA inbox because
  // `legal@` does not exist yet, and a notice address that bounces is a real
  // defect in a signed contract — worse than a shared queue. Split it the day
  // the alias is live; this is the only line that has to change, and the
  // /terms page reads it from here.
  noticeEmail:  RESPONSIBLE_PARTY.contactEmail,
  governingLaw: 'the Republic of South Africa',
  // Courts named for jurisdiction. Gauteng Division seats in Pretoria and is
  // the default for a Gauteng-registered entity.
  courts:       'the High Court of South Africa, Gauteng Division, Pretoria',
} as const
